/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
const sites = require('./sites.js')

const DEFAULT_CACHE = `max-age=${60 * 15}`

module.exports = {
    app: {
        url: {
            site: 'path',
            locale: 'path',
            showDefaults: true
        },
        defaultSite: 'stokke',
        siteAliases: {},
        sites,
        seo: {
            noIndex: true
        },
        commerceAPI: {
            proxyPath: `/mobify/proxy/api`,
            parameters: {
                clientId: 'b878d617-e565-4bf4-a823-c850ce27ec53',
                organizationId: 'f_ecom_aaqf_001',
                shortCode: '9z83zsl4',
                siteId: 'AUT'
            }
        },
        einsteinAPI: {
            host: 'https://api.cquotient.com',
            einsteinId: '79b89e8d-799f-479a-93b1-4dc3741985cc',
            // This differs from the siteId in commerceAPIConfig for testing purposes
            siteId: 'aaqf-AUT',
            isProduction: false
        },
        bazaarvoiceAPI: {
            proxies: {
                prd: {
                    host: 'https://api.bazaarvoice.com',
                    proxyPath: '/proxy/bv/prd'
                },
                stg: {
                    host: 'https://stg.api.bazaarvoice.com',
                    proxyPath: '/proxy/bv/stg'
                }
            },
            apiVersion: '5.4',
            cacheControlHeader: [
                'proxy-revalidate',
                `s-maxage=300`, // 's-maxage=3300',(prd)
                `max-age=90`, // 'max-age=900',
                `stale-while-revalidate=240` // 'stale-while-revalidate=2400'
            ].join(', '),
            ttl: 90 * 1000, // 90s
            throttleInterval: 50
        },
        googleMapsAPI: {
            // ref: https://developers.google.com/maps/documentation/javascript/versions?hl=en#quarterly-updates
            version: 'weekly',
            // ref: https://developers.google.com/maps/documentation/javascript/libraries
            libraries: [
                // ref: https://developers.google.com/maps/documentation/javascript/places
                'places'
            ]
            // region and language parameters are automatically provided based on the locale
            // additional properties used for googlemaps loader
            // ref: https://googlemaps.github.io/js-api-loader/interfaces/LoaderOptions.html
        },
        googleTranslateAPI: {
            host: 'https://translation.googleapis.com/language/translate/v2'
        },
        gtmAPI: {
            gtmId: 'GTM-N6HMPT8'
        },
        curalateAPI: {
            proxyPath: `/mobify/caching/curalate`
        },
        continents: {
            Africa: ['en-EG', 'en-ZA'],
            Asia: [
                'en-QA',
                'en-AE',
                'zh-CN',
                'zh-HK',
                'zh-TW',
                'en-AM',
                'en-AZ',
                'en-GE',
                'en-HK',
                'en-IN',
                'en-IR',
                'en-IL',
                'en-KZ',
                'en-MY',
                'en-QA',
                'en-SG',
                'en-TR',
                'ja-JP',
                'ko-KR',
                'ru-KZ',
                'tr-TR'
            ],
            Europe: [
                'da-DK',
                'nl-BE',
                'nl-NL',
                'en-AL',
                'en-BY',
                'en-BA',
                'en-BG',
                'en-HR',
                'en-CZ',
                'en-CY',
                'en-EE',
                'en-FI',
                'en-GR',
                'en-HU',
                'en-IS',
                'en-IE',
                'en-LV',
                'en-LT',
                'en-PT',
                'en-RO',
                'en-RS',
                'en-SK',
                'en-SI',
                'en-UA',
                'en-GB',
                'fr-BE',
                'fr-FR',
                'fr-LU',
                'fr-CH',
                'de-AT',
                'de-DE',
                'de-LU',
                'de-CH',
                'it-IT',
                'it-CH',
                'no-NO',
                'no-UA',
                'pl-PL',
                'ru-BY',
                'ru-RU',
                'ru-UA',
                'es-ES',
                'sv-SE'
            ],
            North_America: ['en-CA', 'en-US', 'fr-CA', 'es-CR', 'es-MX', 'es-PA', 'es-PR', 'es-US'],
            Oceania: ['en-AU', 'en-NZ'],
            South_America: ['pt-BR', 'es-AR', 'es-CL', 'es-CO', 'es-PE']
        },
        staticAPI: {
            proxyPath: '/mobify/proxy/static'
        },
        storefrontAPI: {
            proxyPath: `/mobify/proxy/sapi`,
            parameters: {
                siteId: 'AUT'
            }
        },
        // cache configuration per page type
        cache: {
            // it's safe to cache cart page as it renderes only the cart skeleton
            cart: 'proxy-revalidate, s-maxage=300, must-revalidate, max-age=60',
            home: DEFAULT_CACHE,
            pdp: DEFAULT_CACHE,
            plp: DEFAULT_CACHE
        },
        // boolean that handles the visibility of stack trace in error page on development
        displayErrorStackTrace: true
    },
    externals: [],
    pageNotFoundURL: '/page-not-found',
    ssrEnabled: true,
    ssrOnly: ['ssr.js', 'ssr.js.map', 'node_modules/**/*.*'],
    ssrShared: [
        'static/ico/favicon.ico',
        'static/apple-developer-merchantid-domain-association.txt',
        'static/robots.txt',
        '**/*.js',
        '**/*.js.map',
        '**/*.json'
    ],
    ssrParameters: {
        ssrFunctionNodeVersion: '18.x',
        proxyConfigs: [
            {
                host: '9z83zsl4.api.commercecloud.salesforce.com',
                path: 'api'
            },
            {
                host: 'aaqf-001.dx.commercecloud.salesforce.com',
                path: 'sapi'
            },
            {
                host: 'aaqf-001.dx.commercecloud.salesforce.com',
                path: 'ocapi'
            },
            {
                host: 'aaqf-001.dx.commercecloud.salesforce.com',
                path: 'static'
            },
            {
                host: 'edge.curalate.com',
                path: 'curalate'
            }
        ]
    }
}
